<template>
  <div class="product-equivalents" :class="{ 'product-equivalents--compact': compact }">
    <div class="form-group mb-3">
      <label class="d-block pe-label">{{ $t('product.equivalent_products') }}</label>
      <v-select
        v-model="selectedProduct"
        :options="availableProducts"
        label="name"
        :filterable="false"
        :clear-search-on-select="false"
        :close-on-select="true"
        :placeholder="$t('product.search_equivalent_product')"
        :disabled="loading || saving || !productId"
        @search="onProductSearch"
        @input="addEquivalent"
      >
        <template #spinner="{ loading }">
          <div v-if="loading || searchingProducts" class="vs__spinner"></div>
        </template>
        <template #option="product">
          <div class="pe-select-option">
            <span>{{ product.name }}</span>
            <small v-if="product.gtin" class="text-muted">GTIN: {{ product.gtin }}</small>
          </div>
        </template>
        <span slot="no-options">{{ noOptionsText }}</span>
      </v-select>
    </div>

    <div v-if="loading" class="d-flex justify-content-center py-3">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <div v-else>
      <div v-if="equivalents.length === 0" class="pe-empty">
        <CIcon name="cil-link" class="mr-1" />
        <span>{{ $t('product.no_equivalent_products') }}</span>
      </div>

      <div v-else>
        <div class="pe-toolbar">
          <span class="pe-count">
            <!-- <CBadge color="secondary">{{ equivalents.length }}</CBadge> -->
            {{ $tc('product.equivalent_products_available', equivalents.length) }}
          </span>
          <small class="text-muted pe-drag-hint">
            <CIcon name="cil-move" size="sm" />
            {{ $t('warehouse.drag_to_reorder') }}
          </small>
        </div>

        <div ref="sortableList" class="pe-list">
          <div
            v-for="(item, index) in equivalents"
            :key="item.id"
            class="pe-row"
            :data-product-id="item.id"
          >
            <button
              type="button"
              class="pe-handle"
              :aria-label="$t('warehouse.drag_to_reorder')"
            >
              <CIcon name="cil-menu" />
            </button>

            <span class="pe-order">{{ index + 1 }}</span>

            <div class="pe-main">
              <strong>{{ item.name }}</strong>
              <small class="text-muted">
                Anyware ID: #{{ item.id }}
                <!-- <template v-if="item.gtin"> / GTIN: {{ item.gtin }}</template> -->
              </small>
            </div>

            <div class="pe-actions">
              <button
                type="button"
                class="pe-action-btn pe-action-btn--danger"
                :disabled="saving"
                :title="$t('global.delete')"
                @click="removeEquivalent(index)"
              >
                <CIcon name="cil-trash" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="d-flex justify-content-end mt-3">
      <CButton
        color="primary"
        :size="compact ? 'sm' : undefined"
        :disabled="loading || saving || !isDirty"
        @click="saveEquivalents"
      >
        <span v-if="saving" class="spinner-border spinner-border-sm mr-1" role="status"></span>
        <CIcon v-else name="cil-save" class="mr-1" />
        {{ $t('global.save') }}
      </CButton>
    </div>
  </div>
</template>

<script>
import vSelect from "vue-select";
import Sortable from "sortablejs";
import "vue-select/dist/vue-select.css";

export default {
  name: "ProductEquivalents",
  components: { vSelect },
  props: {
    productId: { type: [Number, String], default: null },
    firmId: { type: [Number, String], default: null },
    compact: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      saving: false,
      searchingProducts: false,
      productSearch: "",
      searchProductsTimer: null,
      productOptions: [],
      equivalents: [],
      originalIds: [],
      selectedProduct: null,
      sortableInstance: null,
    };
  },
  computed: {
    availableProducts() {
      const selectedIds = new Set(this.equivalents.map((item) => Number(item.id)));
      const currentId = Number(this.productId);

      return this.productOptions.filter((product) => {
        const productId = Number(product.id);
        return productId !== currentId && !selectedIds.has(productId);
      });
    },
    currentIds() {
      return this.equivalents.map((item) => Number(item.id));
    },
    isDirty() {
      return JSON.stringify(this.currentIds) !== JSON.stringify(this.originalIds);
    },
    noOptionsText() {
      if (!this.productSearch || this.productSearch.length < 2) {
        return this.$t('product.search_equivalent_product_min_chars');
      }

      if (this.searchingProducts) {
        return this.$t('global.please_wait');
      }

      return this.$t('global.no_items_found');
    },
  },
  watch: {
    productId: {
      immediate: true,
      handler() {
        this.loadData();
      },
    },
    firmId() {
      this.loadData();
    },
    saving() {
      this.syncSortable();
    },
  },
  beforeDestroy() {
    if (this.searchProductsTimer) {
      clearTimeout(this.searchProductsTimer);
    }

    this.destroySortable();
  },
  methods: {
    async loadData() {
      if (!this.productId || !this.firmId) {
        this.destroySortable();
        return;
      }

      this.loading = true;
      this.destroySortable();

      try {
        this.productOptions = [];
        await this.loadEquivalents();
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          this.syncSortable();
        });
      }
    },
    async searchProducts(search) {
      if (!search || search.length < 2 || !this.firmId) {
        this.productOptions = [];
        return;
      }

      const url = this.$backend
        .get("FIRM", "PRODUCT_LIST", this.firmId)
        .replace("/{related_firm_id}", "");

      this.searchingProducts = true;

      try {
        const response = await this.$axios.get(url, {
          params: {
            type: "supplied-products,own-production",
            orderBy: "name",
            direction: "asc",
            fields: "gtin,net_content,net_content_unit_code",
            search,
            limit: 30,
            excluded_products: JSON.stringify([this.productId]),
          },
        });

        this.productOptions = response.data || [];
      } finally {
        this.searchingProducts = false;
      }
    },
    async loadEquivalents() {
      const url = this.$backend
        .get("PRODUCT", "EQUIVALENTS", this.productId)
        .replace("{firm_id}", this.firmId);

      const response = await this.$axios.get(url);
      this.equivalents = response.data?.data || [];
      this.originalIds = this.currentIds;
    },
    onProductSearch(search) {
      this.productSearch = search || "";

      if (this.searchProductsTimer) {
        clearTimeout(this.searchProductsTimer);
      }

      this.searchProductsTimer = setTimeout(() => {
        this.searchProducts(this.productSearch);
      }, 300);
    },
    addEquivalent(product) {
      if (!product) {
        return;
      }

      this.equivalents.push(product);
      this.selectedProduct = null;
      this.productOptions = this.availableProducts;
      this.$nextTick(() => {
        this.syncSortable();
      });
    },
    removeEquivalent(index) {
      this.equivalents.splice(index, 1);
      this.$nextTick(() => {
        this.syncSortable();
      });
    },
    syncSortable() {
      if (!this.equivalents.length || !this.$refs.sortableList) {
        this.destroySortable();
        return;
      }

      if (this.sortableInstance) {
        this.sortableInstance.option("disabled", this.saving);
        return;
      }

      this.sortableInstance = Sortable.create(this.$refs.sortableList, {
        handle: ".pe-handle",
        animation: 150,
        ghostClass: "pe-ghost",
        chosenClass: "pe-chosen",
        dragClass: "pe-drag",
        onUpdate: ({ oldIndex, newIndex }) => {
          if (oldIndex === newIndex) {
            return;
          }

          this.reorderEquivalents(oldIndex, newIndex);
        },
      });
    },
    destroySortable() {
      if (this.sortableInstance) {
        this.sortableInstance.destroy();
        this.sortableInstance = null;
      }
    },
    reorderEquivalents(oldIndex, newIndex) {
      const equivalents = [...this.equivalents];
      const [movedProduct] = equivalents.splice(oldIndex, 1);

      if (!movedProduct) {
        return;
      }

      equivalents.splice(newIndex, 0, movedProduct);
      this.equivalents = equivalents;
      this.flashRow(movedProduct.id);
    },
    flashRow(productId) {
      this.$nextTick(() => {
        const selector = `[data-product-id="${productId}"]`;
        const element = this.$refs.sortableList?.querySelector(selector);
        if (!element) return;

        element.classList.remove("pe-row--flash");
        void element.offsetWidth;
        element.classList.add("pe-row--flash");
        element.addEventListener("animationend", () => {
          element.classList.remove("pe-row--flash");
        }, { once: true });
      });
    },
    async saveEquivalents() {
      if (!this.productId || !this.firmId) {
        return;
      }

      this.saving = true;

      try {
        const url = this.$backend
          .get("PRODUCT", "EQUIVALENTS", this.productId)
          .replace("{firm_id}", this.firmId);

        const response = await this.$axios.put(url, {
          product_ids: this.currentIds,
        });

        this.equivalents = response.data?.data || [];
        this.originalIds = this.currentIds;
        this.$nextTick(() => {
          this.syncSortable();
        });
        this.$toast.success(this.$t('product.equivalent_products_saved'));
        this.$root.$emit('product-updated', {
          id: Number(this.productId),
          equivalent_products_count: this.equivalents.length,
        });
        this.$emit('saved', this.equivalents);
      } catch (error) {
        this.$toast.error(error.response?.data?.message || this.$t('global.error'));
      } finally {
        this.saving = false;
        this.$nextTick(() => {
          this.syncSortable();
        });
      }
    },
  },
};
</script>

<style scoped>
.product-equivalents {
  width: 100%;
}
.pe-label {
  font-weight: 600;
}
.pe-select-option {
  display: flex;
  flex-direction: column;
}
.pe-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 80px;
  color: #6c757d;
  background: #f8f9fa;
  border: 1px dashed #d8dbe0;
  border-radius: 6px;
}
.pe-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.2rem 0.1rem;
  margin-bottom: 0.35rem;
}
.pe-count {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  min-width: 0;
  color: #6c757d;
  font-size: 0.8rem;
}
.pe-drag-hint {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  white-space: nowrap;
  flex-shrink: 0;
  font-size: 0.75rem;
}
.pe-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 420px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 2px;
}
.pe-list::-webkit-scrollbar {
  width: 5px;
}
.pe-list::-webkit-scrollbar-track {
  background: transparent;
}
.pe-list::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.12);
  border-radius: 3px;
}
.pe-row {
  display: grid;
  grid-template-columns: 28px 28px minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.35rem;
  padding: 0.22rem 0.4rem;
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 0.4rem;
  background: #fff;
  transition: background 0.12s, box-shadow 0.12s;
}
.pe-row:hover {
  background: #f8f9fa;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
.pe-handle {
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 0.3rem;
  background: rgba(108, 117, 125, 0.08);
  color: #6c757d;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  flex-shrink: 0;
  transition: background 0.12s, color 0.12s;
}
.pe-handle:hover {
  background: rgba(23, 162, 184, 0.15);
  color: #0f6c7a;
}
.pe-handle:focus {
  outline: none;
}
.pe-order {
  width: 28px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  background: rgba(0, 0, 0, 0.05);
  color: #6c757d;
  font-size: 0.7rem;
  font-weight: 700;
  flex-shrink: 0;
}
.pe-main {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
}
.pe-main strong,
.pe-main small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pe-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}
.pe-action-btn {
  width: 26px;
  height: 26px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0.3rem;
  background: #f8f9fa;
  color: #495057;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  transition: background 0.1s, color 0.1s, border-color 0.1s;
  line-height: 1;
}
.pe-action-btn:hover:not(:disabled) {
  background: #e9ecef;
  border-color: rgba(0, 0, 0, 0.18);
  color: #212529;
}
.pe-action-btn--danger:hover:not(:disabled) {
  background: rgba(229, 83, 83, 0.1);
  border-color: rgba(229, 83, 83, 0.35);
  color: #e55353;
}
.pe-action-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}
@keyframes pe-row-flash {
  0%   { background: rgba(23, 162, 184, 0.22); }
  60%  { background: rgba(23, 162, 184, 0.12); }
  100% { background: #fff; }
}
.pe-row--flash {
  animation: pe-row-flash 0.7s ease-out forwards;
}
.pe-row.pe-ghost {
  opacity: 0.4;
  background: #e3f7fa;
}
.pe-row.pe-chosen {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}
.pe-row.pe-chosen .pe-handle,
.pe-row.pe-drag .pe-handle {
  cursor: grabbing;
}
.product-equivalents--compact .pe-row {
  grid-template-columns: 26px 24px minmax(0, 1fr) auto;
  gap: 0.25rem;
  padding: 0.18rem 0.3rem;
}
.product-equivalents--compact .pe-handle {
  width: 26px;
  height: 26px;
}
.product-equivalents--compact .pe-order {
  width: 24px;
  font-size: 0.65rem;
}
.product-equivalents--compact .pe-actions {
  gap: 0;
}
.product-equivalents--compact .pe-list {
  max-height: 320px;
}
@media (max-width: 575px) {
  .pe-drag-hint {
    display: none;
  }

  .pe-row {
    grid-template-columns: 26px 24px minmax(0, 1fr) auto;
    gap: 0.25rem;
    padding: 0.18rem 0.3rem;
  }

  .pe-handle {
    width: 26px;
    height: 26px;
  }

  .pe-order {
    width: 24px;
    font-size: 0.65rem;
  }

  .pe-main strong,
  .pe-main small {
    white-space: normal;
  }
}
</style>
